import styles from "./FileBlock.module.css"
import { filesvg } from "../../assets/svg/svgimages"
import { useDispatch, useSelector } from "react-redux";
import { downloadAsset, downloadProductAssetsArchive } from "../../store/slices/singleProductSlice";
import { setAuthRedirectPath } from "../../store/userSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useMemo, useEffect, useRef } from "react";
import DownloadIconSvg from "./BtnIcons.svg";
import VectorIconSvg from "./Vector.svg";
import FormatsRecallControl from "./FormatsRecallControl";
import { API_ORIGIN } from "../../config";
import { buildAssetAnalyticsProps, buildProductAnalyticsProps, capture } from "../../analytics";

const LockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const ClearIcon = () => (
  <svg width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const SearchIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="8.5" cy="8.5" r="5.75" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12.7 12.7L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const getFormatResolutionLabel = (format) => {
  const width = Number(format?.res_width);
  const height = Number(format?.res_height);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return "";
  }
  return `${width}\u00D7${height}`;
};

const getAssetLabel = (asset) => {
  const typeName = (asset.asset_type?.name || '').toUpperCase();
  const ext = (asset.name || '').split('.').pop()?.toLowerCase() || '';

  if (typeName.includes('GRID')) {
    return ext === 'zip' ? 'Grid Pack' : 'Grid inf.';
  }
  if (typeName === 'PRESET') return '3DE Preset';
  if (typeName === '3DE PROJECT') return '3DE Project';
  if (typeName === 'NUKE SCRIPT') return 'Nuke LD Node';

  // Fallback: capitalize each word
  return typeName.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) || 'Asset';
};

// file_size from API is in bytes (File.file_size raw value)
export const formatFileSize = (bytes) => {
  if (bytes == null || bytes <= 0) return null;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const getFileExtension = (filename) => {
  if (!filename || typeof filename !== 'string') return null;
  const lastDot = filename.lastIndexOf('.');
  if (lastDot <= 0) return null;
  const ext = filename.slice(lastDot + 1);
  if (!ext) return null;
  return ext.toUpperCase();
};

const SUPPORTED_VARIANT_ASSET_TYPES = new Set(['PRESET', 'NUKE SCRIPT']);

const getAssetTypeName = (asset) => (asset?.asset_type?.name || '').trim().toUpperCase();

const supportsVariantBadges = (asset) => SUPPORTED_VARIANT_ASSET_TYPES.has(getAssetTypeName(asset));

export const getAssetSuffix = (filename, serialNumber) => {
  if (!filename || typeof filename !== 'string') return '';

  const extractDisplayableSuffix = (rawSuffix) => {
    if (!rawSuffix) return '';
    const normalizedSuffix = rawSuffix
      .replace(/^ld_node_+/i, '')
      .replace(/^preset_+/i, '')
      .replace(/^_+/, '')
      .split('.')[0];

    const dgNormalizeMatch = normalizedSuffix.match(/^(dg\d+)_normalize$/i);
    if (dgNormalizeMatch) {
      return `${dgNormalizeMatch[1].toLowerCase()}_normalize`;
    }

    const dgMatch = normalizedSuffix.match(/^(dg\d+)$/i);
    if (dgMatch) {
      return dgMatch[1].toLowerCase();
    }

    return '';
  };

  const lastDot = filename.lastIndexOf('.');
  const nameWithoutExt = lastDot <= 0 ? filename : filename.substring(0, lastDot);

  if (serialNumber !== undefined && serialNumber !== null) {
    const serialStr = String(serialNumber).trim();
    if (serialStr) {
      const lowerName = nameWithoutExt.toLowerCase();
      const lowerSerial = serialStr.toLowerCase();
      const serialIdx = lowerName.lastIndexOf(lowerSerial);

      if (serialIdx !== -1) {
        const suffix = nameWithoutExt.substring(serialIdx + serialStr.length).replace(/^_+/, '');
        return extractDisplayableSuffix(suffix);
      }
    }
  }

  const dgNormalizeFallbackMatch = nameWithoutExt.match(/(?:^|_)(dg\d+_normalize)$/i);
  if (dgNormalizeFallbackMatch) {
    return dgNormalizeFallbackMatch[1].toLowerCase();
  }

  const dgFallbackMatch = nameWithoutExt.match(/(?:^|_)(dg\d+)$/i);
  if (dgFallbackMatch) {
    return dgFallbackMatch[1].toLowerCase();
  }

  return '';
};

export const getVariantBadgeAvailabilityByType = (assets, serialNumber) => {
  const variantSetsByType = assets.reduce((acc, asset) => {
    if (!supportsVariantBadges(asset)) {
      return acc;
    }

    const suffix = getAssetSuffix(asset?.name, serialNumber);
    if (!suffix) {
      return acc;
    }

    const assetTypeName = getAssetTypeName(asset);
    if (!acc[assetTypeName]) {
      acc[assetTypeName] = new Set();
    }
    acc[assetTypeName].add(suffix);
    return acc;
  }, {});

  return Object.entries(variantSetsByType).reduce((acc, [assetTypeName, suffixes]) => {
    acc[assetTypeName] = suffixes.size > 1;
    return acc;
  }, {});
};

const toAbsoluteAssetUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
};

export const resolvePreviewUrlForAsset = (asset, singleProduct, fallbackPreviewUrl = null) => {
  const primaryFormatName = getPrimaryFormatName(asset);
  const modePreviewUrl = toAbsoluteAssetUrl(
    singleProduct?.formats?.find((format) => format?.name === primaryFormatName)?.preview_url
  );
  if (modePreviewUrl) {
    return modePreviewUrl;
  }
  return fallbackPreviewUrl || null;
};

const normalizeNumericId = (value) => {
  if (value === undefined || value === null) return undefined;
  const numeric = Number(value);
  if (Number.isInteger(numeric) && numeric > 0) return numeric;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const lowered = trimmed.toLowerCase();
    if (lowered === "undefined" || lowered === "null") return undefined;
    if (/^\d+$/.test(trimmed)) {
      const parsed = Number(trimmed);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
    }
  }
  return undefined;
};

const normalizeUuid = (value) => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(trimmed) ? trimmed.toLowerCase() : undefined;
};

const sanitizeFilenameCandidate = (value) => {
  if (value === undefined || value === null) return undefined;
  const asString = String(value).trim();
  if (!asString) return undefined;
  const lowered = asString.toLowerCase();
  if (lowered === "undefined" || lowered === "null") return undefined;
  return asString;
};

const resolveAssetId = (asset) => {
  const identifier = asset?.download_identifier;
  if (identifier) {
    const numericId = normalizeNumericId(identifier);
    if (numericId !== undefined) return numericId;
    const uuidId = normalizeUuid(identifier);
    if (uuidId) return uuidId;
  }
  const candidates = [asset?.id, asset?.asset_id, asset?.asset?.id, asset?.file?.id, asset?.pk, asset?.file_id];
  for (const candidate of candidates) {
    const normalized = normalizeNumericId(candidate);
    if (normalized !== undefined && normalized !== null) return normalized;
  }
  return normalizeUuid(asset?.file?.id) || normalizeUuid(asset?.file_id) || normalizeUuid(asset?.file?.file_id);
};

const resolveAssetFilename = (asset, preResolvedId, singleProduct) => {
  const candidates = [asset?.download_filename, asset?.file?.name, asset?.name, asset?.file?.file];
  for (const candidate of candidates) {
    const sanitized = sanitizeFilenameCandidate(candidate);
    if (sanitized) return sanitized;
  }
  const fallbackId = normalizeNumericId(preResolvedId ?? resolveAssetId(asset)) ??
    (singleProduct?.slug ? `product-${singleProduct.slug}` : singleProduct?.id);
  return fallbackId ? `asset-${fallbackId}` : undefined;
};

const INITIAL_VISIBLE_FORMAT_GROUPS = 3;
const LOAD_MORE_FORMAT_GROUPS = 3;
const DESKTOP_RECALL_DOCK_TOP = 132;
const DESKTOP_RECALL_TRIGGER_TOP = 104;

const getPrimaryFormatName = (asset) => asset.formats?.[0]?.name || 'Other formats';

const ASYNC_PREPARED_EXTENSIONS = new Set(['JPG', 'JPEG', 'ZIP']);

export const shouldShowFormatsRecallControl = ({ viewportWidth, assetsInView, selectorInView }) => {
  if (viewportWidth < 1025) {
    return false;
  }

  return assetsInView && !selectorInView;
};

const FileBlock = ({ singleProduct, previewUrl, onPreviewClick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user: currentUser } = useSelector((state) => state.user);
  const { purchasedProducts } = useSelector((state) => state.profileProduct);
  const [selectedFormat, setSelectedFormat] = useState('all');
  const [appliedFormat, setAppliedFormat] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [downloadingAssets, setDownloadingAssets] = useState({});
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [showDownloadAllPreparing, setShowDownloadAllPreparing] = useState(false);
  const [isGridEntering, setIsGridEntering] = useState(false);
  const [lockedContainerHeight, setLockedContainerHeight] = useState(null);
  const [visibleFormatGroupCount, setVisibleFormatGroupCount] = useState(INITIAL_VISIBLE_FORMAT_GROUPS);
  const [hasExpandedAllFormats, setHasExpandedAllFormats] = useState(false);
  const [showRecallControl, setShowRecallControl] = useState(false);
  const [isRecallControlOpen, setIsRecallControlOpen] = useState(false);
  const [recallControlMetrics, setRecallControlMetrics] = useState({ left: 0, width: 0 });
  const assetsContainerRef = useRef(null);
  const assetsListRef = useRef(null);
  const formatsStageRef = useRef(null);
  const formatsFilterRef = useRef(null);
  const assetsInViewRef = useRef(false);
  const selectorInViewRef = useRef(true);
  const formatCommitTimeoutRef = useRef(null);
  const scrollSettleCleanupRef = useRef(null);
  const heightAnimationFrameRef = useRef(null);
  const pendingContainerTransitionRef = useRef(null);
  const containerTransitionCleanupRef = useRef(null);
  const containerTransitionFallbackRef = useRef(null);
  const lockTooltipTimeoutRef = useRef(null);
  const downloadAllPreparingTimerRef = useRef(null);
  const [visibleLockTooltipId, setVisibleLockTooltipId] = useState(null);

  const variantBadgeAvailabilityByType = useMemo(
    () => getVariantBadgeAvailabilityByType(singleProduct?.assets || [], singleProduct?.serial_number),
    [singleProduct?.assets, singleProduct?.serial_number]
  );

  useEffect(() => {
    const listEl = assetsListRef.current;
    if (!listEl) return;
    listEl.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    return () => {
      if (formatCommitTimeoutRef.current) {
        window.clearTimeout(formatCommitTimeoutRef.current);
      }
      if (scrollSettleCleanupRef.current) {
        scrollSettleCleanupRef.current();
      }
      if (heightAnimationFrameRef.current) {
        window.cancelAnimationFrame(heightAnimationFrameRef.current);
      }
      if (containerTransitionCleanupRef.current) {
        containerTransitionCleanupRef.current();
      }
      if (containerTransitionFallbackRef.current) {
        window.clearTimeout(containerTransitionFallbackRef.current);
      }
      if (lockTooltipTimeoutRef.current) {
        window.clearTimeout(lockTooltipTimeoutRef.current);
      }
      if (downloadAllPreparingTimerRef.current) {
        window.clearTimeout(downloadAllPreparingTimerRef.current);
      }
    };
  }, []);

  const clearDownloadAllPreparingTimer = () => {
    if (downloadAllPreparingTimerRef.current) {
      window.clearTimeout(downloadAllPreparingTimerRef.current);
      downloadAllPreparingTimerRef.current = null;
    }
  };

  const showDownloadAllPreparingBriefly = () => {
    clearDownloadAllPreparingTimer();
    setShowDownloadAllPreparing(true);
    downloadAllPreparingTimerRef.current = window.setTimeout(() => {
      setShowDownloadAllPreparing(false);
      downloadAllPreparingTimerRef.current = null;
    }, 4000);
  };

  const prefersReducedMotion = () => (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  const clearFormatTransitionTimers = () => {
    if (formatCommitTimeoutRef.current) {
      window.clearTimeout(formatCommitTimeoutRef.current);
      formatCommitTimeoutRef.current = null;
    }
    if (scrollSettleCleanupRef.current) {
      scrollSettleCleanupRef.current();
      scrollSettleCleanupRef.current = null;
    }
    if (heightAnimationFrameRef.current) {
      window.cancelAnimationFrame(heightAnimationFrameRef.current);
      heightAnimationFrameRef.current = null;
    }
    if (containerTransitionCleanupRef.current) {
      containerTransitionCleanupRef.current();
      containerTransitionCleanupRef.current = null;
    }
    if (containerTransitionFallbackRef.current) {
      window.clearTimeout(containerTransitionFallbackRef.current);
      containerTransitionFallbackRef.current = null;
    }
    pendingContainerTransitionRef.current = null;
    setIsGridEntering(false);
    setLockedContainerHeight(null);
  };

  const scrollToAssetsBlockTop = (behavior) => {
    const assetsSection = assetsContainerRef.current?.closest?.("#assets");
    const targetElement = assetsSection?.querySelector?.("[data-assets-scroll-anchor]") || assetsContainerRef.current;
    if (!targetElement) {
      return;
    }

    if (typeof targetElement.scrollIntoView === "function") {
      targetElement.scrollIntoView({
        behavior,
        block: "start",
      });
      return;
    }

    window.scrollTo({ top: 0, behavior });
  };

  const commitAppliedFormat = (nextFormat) => {
    setAppliedFormat(nextFormat);
  };

  const estimatePanelHeightForFormat = (formatName) => {
    const listEl = assetsListRef.current;
    const formatsHeight = formatsStageRef.current?.getBoundingClientRect?.().height || 0;
    if (!listEl || !formatName || formatName === 'all') {
      return formatsHeight || null;
    }

    const computedStyle = window.getComputedStyle(listEl);
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

    const headerEl = listEl.querySelector(`.${styles.assetsHeader}`);
    const headerRect = headerEl?.getBoundingClientRect?.();
    const headerStyle = headerEl ? window.getComputedStyle(headerEl) : null;
    const headerHeight = headerRect?.height || 0;
    const headerMarginTop = headerStyle ? (parseFloat(headerStyle.marginTop) || 0) : 0;
    const headerMarginBottom = headerStyle ? (parseFloat(headerStyle.marginBottom) || 0) : 0;

    const firstRowEl = listEl.querySelector(`.${styles.assetItem}`);
    const rowHeight = firstRowEl?.getBoundingClientRect?.().height || 60;

    const matchingAssetCount = productAssets.filter((asset) => (
      asset.formats &&
      Array.isArray(asset.formats) &&
      asset.formats.some((format) => format && format.name === formatName)
    )).length;

    const emptyStateHeight = 72;
    const assetsHeight = matchingAssetCount > 0 ? matchingAssetCount * rowHeight : emptyStateHeight;
    const estimatedAssetsPanelHeight = paddingTop + paddingBottom + headerHeight + headerMarginTop + headerMarginBottom + assetsHeight;

    return Math.max(estimatedAssetsPanelHeight, formatsHeight, 200);
  };

  const beginFormatSwap = (nextFormat) => {
    const containerHeight = assetsContainerRef.current?.getBoundingClientRect?.().height;
    if (containerHeight) {
      setLockedContainerHeight(containerHeight);
    }

    pendingContainerTransitionRef.current = nextFormat;
    setIsGridEntering(false);
    commitAppliedFormat(nextFormat);
  };

  const waitForScrollSettle = (onSettled) => {
    if (typeof window === "undefined") {
      onSettled();
      return;
    }

    let finished = false;
    let idleTimeoutId = null;
    let maxTimeoutId = null;

    const finish = () => {
      if (finished) return;
      finished = true;
      if (formatCommitTimeoutRef.current) {
        window.clearTimeout(formatCommitTimeoutRef.current);
        formatCommitTimeoutRef.current = null;
      }
      if (scrollSettleCleanupRef.current) {
        scrollSettleCleanupRef.current();
        scrollSettleCleanupRef.current = null;
      }
      if (idleTimeoutId) {
        window.clearTimeout(idleTimeoutId);
      }
      if (maxTimeoutId) {
        window.clearTimeout(maxTimeoutId);
      }
      onSettled();
    };

    const armIdleTimer = () => {
      if (idleTimeoutId) {
        window.clearTimeout(idleTimeoutId);
      }
      idleTimeoutId = window.setTimeout(finish, 90);
    };

    const handleScroll = () => {
      armIdleTimer();
    };

    const handleScrollEnd = () => {
      finish();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("scrollend", handleScrollEnd, { once: true });
    scrollSettleCleanupRef.current = () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scrollend", handleScrollEnd);
    };

    armIdleTimer();
    maxTimeoutId = window.setTimeout(finish, 700);
    formatCommitTimeoutRef.current = maxTimeoutId;
  };

  const requestFormatChange = (nextFormat, { closeMobileFilters = false } = {}) => {
    const sameSelectedFormat = nextFormat === selectedFormat;
    const sameAppliedFormat = nextFormat === appliedFormat;

    if (sameSelectedFormat && sameAppliedFormat) {
      if (closeMobileFilters) {
        setShowMobileFilters(false);
      }
      return;
    }

    const reduceMotion = prefersReducedMotion();
    const listEl = assetsListRef.current;

    clearFormatTransitionTimers();

    if (listEl) {
      listEl.scrollTop = 0;
    }

    if (nextFormat === 'all') {
      setVisibleFormatGroupCount(INITIAL_VISIBLE_FORMAT_GROUPS);
    }

    setSelectedFormat(nextFormat);
    if (closeMobileFilters) {
      setShowMobileFilters(false);
    }

    if (reduceMotion) {
      scrollToAssetsBlockTop("auto");
      window.requestAnimationFrame(() => {
        commitAppliedFormat(nextFormat);
      });
      return;
    }

    const shouldUseDeferredShrinkSwap =
      typeof window !== "undefined" &&
      window.innerWidth >= 1025 &&
      appliedFormat === 'all' &&
      nextFormat !== 'all';

    if (shouldUseDeferredShrinkSwap) {
      const containerEl = assetsContainerRef.current;
      const currentHeight = containerEl?.getBoundingClientRect?.().height || 0;
      const targetHeight = estimatePanelHeightForFormat(nextFormat) || currentHeight;

      if (containerEl && currentHeight > 0 && targetHeight < currentHeight - 1) {
        setLockedContainerHeight(currentHeight);
        setIsGridEntering(false);

        window.requestAnimationFrame(() => {
          scrollToAssetsBlockTop("smooth");

          const finalizeSwap = () => {
            if (containerTransitionCleanupRef.current) {
              containerTransitionCleanupRef.current();
              containerTransitionCleanupRef.current = null;
            }
            if (containerTransitionFallbackRef.current) {
              window.clearTimeout(containerTransitionFallbackRef.current);
              containerTransitionFallbackRef.current = null;
            }

            commitAppliedFormat(nextFormat);

            window.requestAnimationFrame(() => {
              setLockedContainerHeight(null);
              setIsGridEntering(true);
            });
          };

          const handleTransitionEnd = (event) => {
            if (event.target !== containerEl || event.propertyName !== 'height') {
              return;
            }
            finalizeSwap();
          };

          containerEl.addEventListener('transitionend', handleTransitionEnd);
          containerTransitionCleanupRef.current = () => {
            containerEl.removeEventListener('transitionend', handleTransitionEnd);
          };
          containerTransitionFallbackRef.current = window.setTimeout(finalizeSwap, 420);

          setLockedContainerHeight(targetHeight);
        });

        return;
      }
    }

    window.requestAnimationFrame(() => {
      scrollToAssetsBlockTop("smooth");
      waitForScrollSettle(() => {
        beginFormatSwap(nextFormat);
      });
    });
  };

  const clearLockTooltipTimeout = () => {
    if (lockTooltipTimeoutRef.current) {
      window.clearTimeout(lockTooltipTimeoutRef.current);
      lockTooltipTimeoutRef.current = null;
    }
  };

  const openLockTooltip = (tooltipId, { autoHide = false } = {}) => {
    setVisibleLockTooltipId(tooltipId);
    clearLockTooltipTimeout();

    if (autoHide) {
      lockTooltipTimeoutRef.current = window.setTimeout(() => {
        setVisibleLockTooltipId((currentId) => currentId === tooltipId ? null : currentId);
        lockTooltipTimeoutRef.current = null;
      }, 2500);
    }
  };

  const closeLockTooltip = (tooltipId) => {
    setVisibleLockTooltipId((currentId) => currentId === tooltipId ? null : currentId);
    clearLockTooltipTimeout();
  };

  const toggleLockTooltip = (tooltipId) => {
    if (visibleLockTooltipId === tooltipId) {
      closeLockTooltip(tooltipId);
      return;
    }
    openLockTooltip(tooltipId, { autoHide: true });
  };

  const productAccessType = singleProduct?.access_type?.toLowerCase?.() || '';
  const productIsFree = productAccessType === 'free';

  const isProductPurchased = useMemo(() => {
    if (singleProduct?.is_purchased !== undefined) return Boolean(singleProduct.is_purchased);
    if (!singleProduct?.id) return false;
    const list = purchasedProducts?.results;
    return Array.isArray(list) ? list.some(item => item.id === singleProduct.id) : false;
  }, [singleProduct?.is_purchased, purchasedProducts, singleProduct?.id]);

  const isProductAuthor = Boolean(
    currentUser?.id && singleProduct?.author?.id && currentUser.id === singleProduct.author.id
  );
  const hasInternalAccess = Boolean(singleProduct?.internal_access);

  const isAssetFree = (asset) => Boolean(productIsFree || asset?.is_free || asset?.is_free_asset);

  // Access is decided by the backend and arrives as can_download. The client used
  // to unlock buttons on is_staff, which the backend stopped honouring, so the
  // button looked enabled and then 403'd on click.
  const isAssetAccessible = (asset) => Boolean(
    isProductAuthor || asset?.can_download || isAssetFree(asset)
  );

  const canDownloadAsset = (asset) => isAuthenticated && isAssetAccessible(asset);

  const getLockTooltip = (asset) => {
    if (singleProduct.purchased_tier === 'grids_only' && asset.asset_type?.is_grid === false) {
      return 'Available in Full package';
    }
    return 'Available after purchase';
  };

  const handleDownloadAsset = async (asset) => {
    if (!isAuthenticated) {
      dispatch(setAuthRedirectPath(location.pathname + location.search));
      navigate("/authorization");
      return;
    }
    const assetId = resolveAssetId(asset);
    const assetName = resolveAssetFilename(asset, assetId, singleProduct);
    const identifierValid = typeof assetId === "number"
      ? Number.isInteger(assetId) && assetId >= 0
      : typeof assetId === "string"
        ? (() => {
            const trimmed = assetId.trim();
            if (!trimmed) return false;
            const lowered = trimmed.toLowerCase();
            if (lowered === "undefined" || lowered === "null") return false;
            if (/^\d+$/.test(trimmed)) return true;
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            return uuidRegex.test(trimmed);
          })()
        : false;

    if (!identifierValid || !assetName) return;
    if (!isAssetAccessible(asset)) return;

    const key = assetId;
    capture("asset_download_clicked", {
      ...buildProductAnalyticsProps(singleProduct),
      ...buildAssetAnalyticsProps(asset),
    });
    setDownloadingAssets(prev => ({ ...prev, [key]: true }));
    try {
      await dispatch(downloadAsset({
        assetId,
        assetName,
        productId: productIsFree ? singleProduct?.id : undefined,
      })).unwrap();
    } catch (error) {
      capture("download_failed", {
        ...buildProductAnalyticsProps(singleProduct),
        ...buildAssetAnalyticsProps(asset),
        download_scope: "single_asset",
        error_message: typeof error === "string" ? error : error?.message,
      });
    } finally {
      setDownloadingAssets(prev => ({ ...prev, [key]: false }));
    }
  };

  // hasInternalAccess replaces the old is_staff check here too: the bundle
  // endpoint honours a staff grant, so the button is real, but the grant comes
  // from the backend rather than from a client-side flag.
  const canDownloadAll = isAuthenticated && (isProductPurchased || isProductAuthor || hasInternalAccess || productIsFree);
  const headerDownloadLabel = appliedFormat === 'all' ? 'Download all files' : 'Download this format';
  const headerDownloadingLabel = 'Preparing download';

  const handleDownloadAll = async () => {
    if (!canDownloadAll || !singleProduct?.id || isDownloadingAll) return;
    setIsDownloadingAll(true);
    showDownloadAllPreparingBriefly();
    capture("product_download_clicked", {
      ...buildProductAnalyticsProps(singleProduct),
      download_scope: appliedFormat === 'all' ? 'all_assets' : 'format_assets',
      format_name: appliedFormat === 'all' ? null : appliedFormat,
      is_authenticated: isAuthenticated,
    });
    try {
      await dispatch(downloadProductAssetsArchive({
        productId: singleProduct.id,
        slug: singleProduct.slug,
        formatName: appliedFormat === 'all' ? null : appliedFormat,
      })).unwrap();
    } catch (err) {
      console.error('Download all failed:', err);
      capture("download_failed", {
        ...buildProductAnalyticsProps(singleProduct),
        download_scope: appliedFormat === 'all' ? 'all_assets' : 'format_assets',
        format_name: appliedFormat === 'all' ? null : appliedFormat,
        error_message: typeof err === "string" ? err : err?.message,
      });
    } finally {
      clearDownloadAllPreparingTimer();
      setShowDownloadAllPreparing(false);
      setIsDownloadingAll(false);
    }
  };

  const handleFormatSelect = (format) => {
    requestFormatChange(format, { closeMobileFilters: showMobileFilters });
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    requestFormatChange('all');
  };

  const productAssets = Array.isArray(singleProduct?.assets) ? singleProduct.assets : [];
  const allFormatsFromApi = Array.isArray(singleProduct?.formats)
    ? singleProduct.formats.filter(format => format?.name)
    : [];
  const formatMetaByName = new Map(allFormatsFromApi.map(format => [format.name, format]));
  const allFormats = ['all', ...allFormatsFromApi.map(format => format.name)];

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredFormats = normalizedQuery
    ? allFormats.filter(format => format === 'all' || format.toLowerCase().includes(normalizedQuery))
    : allFormats;

  const filteredAssets = productAssets
    .filter(asset => {
      const matchesFormat =
        appliedFormat === 'all' ||
        (asset.formats && Array.isArray(asset.formats) &&
          asset.formats.some(format => format && format.name === appliedFormat));
      return matchesFormat;
    });
  const selectedFormatLabel = selectedFormat === 'all' ? 'All formats' : selectedFormat;
  const selectedFormatResolution = selectedFormat === 'all'
    ? ''
    : getFormatResolutionLabel(formatMetaByName.get(selectedFormat));
  const appliedFormatResolution = appliedFormat === 'all'
    ? ''
    : getFormatResolutionLabel(formatMetaByName.get(appliedFormat));
  const allFormatGroups = useMemo(() => {
    if (appliedFormat !== 'all') return [];

    const seenGroups = new Set();
    return filteredAssets.reduce((groups, asset) => {
      const groupName = getPrimaryFormatName(asset);
      if (seenGroups.has(groupName)) return groups;

      seenGroups.add(groupName);
      groups.push({
        name: groupName,
        resolution: getFormatResolutionLabel(asset.formats?.[0] || null),
      });
      return groups;
    }, []);
  }, [appliedFormat, filteredAssets]);

  const visibleAllFormatGroupNames = useMemo(() => {
    if (appliedFormat !== 'all') return null;
    return new Set(
      allFormatGroups
        .slice(0, visibleFormatGroupCount)
        .map((group) => group.name),
    );
  }, [appliedFormat, allFormatGroups, visibleFormatGroupCount]);

  const visibleFilteredAssets = useMemo(() => {
    if (appliedFormat !== 'all' || !visibleAllFormatGroupNames) {
      return filteredAssets;
    }

    return filteredAssets.filter((asset) => visibleAllFormatGroupNames.has(getPrimaryFormatName(asset)));
  }, [appliedFormat, filteredAssets, visibleAllFormatGroupNames]);

  const remainingFormatGroupCount = appliedFormat === 'all'
    ? Math.max(allFormatGroups.length - visibleFormatGroupCount, 0)
    : 0;
  const nextRevealGroupCount = Math.min(LOAD_MORE_FORMAT_GROUPS, remainingFormatGroupCount);
  const loadMoreFormatsLabel = nextRevealGroupCount === 1
    ? 'Load 1 more format'
    : `Load ${nextRevealGroupCount} more formats`;

  useEffect(() => {
    const pendingFormat = pendingContainerTransitionRef.current;
    if (!pendingFormat || pendingFormat !== appliedFormat) {
      return;
    }

    const containerEl = assetsContainerRef.current;
    const assetsListHeight = assetsListRef.current?.scrollHeight || 0;
    const formatsHeight = formatsStageRef.current?.getBoundingClientRect?.().height || 0;
    const targetHeight = Math.max(assetsListHeight, formatsHeight);

    pendingContainerTransitionRef.current = null;

    if (!containerEl || !targetHeight) {
      setLockedContainerHeight(null);
      setIsGridEntering(false);
      return;
    }

    const currentHeight = containerEl.getBoundingClientRect?.().height || 0;

    const releaseLockedHeight = () => {
      if (containerTransitionCleanupRef.current) {
        containerTransitionCleanupRef.current();
        containerTransitionCleanupRef.current = null;
      }
      if (containerTransitionFallbackRef.current) {
        window.clearTimeout(containerTransitionFallbackRef.current);
        containerTransitionFallbackRef.current = null;
      }
      setLockedContainerHeight(null);
      setIsGridEntering(false);
    };

    heightAnimationFrameRef.current = window.requestAnimationFrame(() => {
      setLockedContainerHeight(targetHeight);
      setIsGridEntering(true);
      heightAnimationFrameRef.current = null;

      if (Math.abs(currentHeight - targetHeight) < 1) {
        window.requestAnimationFrame(() => {
          releaseLockedHeight();
        });
        return;
      }

      const handleTransitionEnd = (event) => {
        if (event.target !== containerEl || event.propertyName !== 'height') {
          return;
        }
        releaseLockedHeight();
      };

      containerEl.addEventListener('transitionend', handleTransitionEnd);
      containerTransitionCleanupRef.current = () => {
        containerEl.removeEventListener('transitionend', handleTransitionEnd);
      };

      // Safety fallback in case the browser skips transitionend.
      containerTransitionFallbackRef.current = window.setTimeout(() => {
        releaseLockedHeight();
      }, 420);
    });
  }, [appliedFormat, visibleFilteredAssets.length, remainingFormatGroupCount]);

  useEffect(() => {
    if (appliedFormat === 'all' && selectedFormat === 'all') {
      setVisibleFormatGroupCount(INITIAL_VISIBLE_FORMAT_GROUPS);
    }
  }, [searchQuery, singleProduct?.id, appliedFormat, selectedFormat]);

  useEffect(() => {
    setHasExpandedAllFormats(false);
  }, [singleProduct?.id]);

  const recallControlEnabled = allFormatsFromApi.length > 1 && hasExpandedAllFormats;

  useEffect(() => {
    if (!recallControlEnabled || typeof window === "undefined") {
      setShowRecallControl(false);
      return undefined;
    }

    const updateRecallMetrics = () => {
      if (window.innerWidth < 1025) {
        setShowRecallControl(false);
        setRecallControlMetrics({ left: 0, width: 0 });
        return;
      }

      const stageRect = formatsStageRef.current?.getBoundingClientRect?.();
      if (!stageRect) {
        return;
      }

      setRecallControlMetrics({
        left: Math.max(stageRect.left + 20, 16),
        width: Math.max(stageRect.width - 40, 220),
      });
    };

    const commitRecallVisibility = () => {
      setShowRecallControl(shouldShowFormatsRecallControl({
        viewportWidth: window.innerWidth,
        assetsInView: assetsInViewRef.current,
        selectorInView: selectorInViewRef.current,
      }));
    };

    updateRecallMetrics();
    commitRecallVisibility();

    let assetsObserver = null;
    let selectorObserver = null;

    if (typeof window.IntersectionObserver === "function") {
      assetsObserver = new window.IntersectionObserver(
        ([entry]) => {
          assetsInViewRef.current = entry.isIntersecting;
          commitRecallVisibility();
        },
        {
          root: null,
          rootMargin: `-${DESKTOP_RECALL_DOCK_TOP}px 0px -140px 0px`,
          threshold: 0,
        },
      );

      selectorObserver = new window.IntersectionObserver(
        ([entry]) => {
          selectorInViewRef.current = entry.isIntersecting;
          commitRecallVisibility();
        },
        {
          root: null,
          rootMargin: `-${DESKTOP_RECALL_TRIGGER_TOP}px 0px -45% 0px`,
          threshold: 0,
        },
      );

      if (assetsContainerRef.current) {
        assetsObserver.observe(assetsContainerRef.current);
      }

      if (formatsFilterRef.current) {
        selectorObserver.observe(formatsFilterRef.current);
      }
    }

    const handleResize = () => {
      updateRecallMetrics();
      commitRecallVisibility();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      assetsObserver?.disconnect?.();
      selectorObserver?.disconnect?.();
      window.removeEventListener("resize", handleResize);
    };
  }, [recallControlEnabled, appliedFormat, searchQuery, visibleFormatGroupCount]);

  useEffect(() => {
    if (!showRecallControl) {
      setIsRecallControlOpen(false);
    }
  }, [showRecallControl]);

  useEffect(() => {
    setIsRecallControlOpen(false);
  }, [selectedFormat]);

  if (productAssets.length === 0) {
    return (
      <div className={styles.assetsContainer}>
        <p className={styles.emptyStateText}>
          This product has no downloadable files yet.
        </p>
      </div>
    );
  }

  const handleLoadMoreFormats = () => {
    setHasExpandedAllFormats(true);
    setVisibleFormatGroupCount((currentCount) => Math.min(
      currentCount + LOAD_MORE_FORMAT_GROUPS,
      allFormatGroups.length,
    ));
  };

  const handleRecallFormatSelect = (format) => {
    setIsRecallControlOpen(false);
    requestFormatChange(format);
  };

  return (
    <div
      ref={assetsContainerRef}
      data-testid="file-block-assets-container"
      className={`${styles.assetsContainer} ${showMobileFilters ? styles.showFilters : ''} ${lockedContainerHeight !== null ? styles.assetsContainerHeightLocked : ''}`}
      style={lockedContainerHeight !== null ? { height: `${lockedContainerHeight}px` } : undefined}
    >
      {/* Mobile filter button */}
      {!showMobileFilters && (
        <div className={styles.mobileFilterPanel}>
          <button
            type="button"
            className={styles.mobileFilterButton}
            onClick={() => setShowMobileFilters(true)}
            aria-expanded={showMobileFilters}
            aria-controls="file-block-formats"
            aria-label="Filter by format"
          >
            <img src={VectorIconSvg} alt="" className={styles.vectorIcon} />
            <span className={styles.mobileFilterLabelGroup}>
              <span className={styles.mobileFilterLabel}>{selectedFormatLabel}</span>
              {selectedFormatResolution && (
                <span className={styles.mobileFilterMeta}>{selectedFormatResolution}</span>
              )}
            </span>
          </button>
        </div>
      )}

      {/* Mobile search */}
      {showMobileFilters && (
        <div className={styles.mobileSearchContainer}>
          <div className={styles.mobileSearchInputWrapper}>
            <input
              type="text"
              placeholder="Filter formats"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.mobileSearchInput}
              aria-label="Search formats"
            />
            <button
              type="button"
              className={styles.mobileSearchButton}
              onClick={handleClearSearch}
              aria-label={searchQuery ? 'Clear format filter' : 'Filter formats'}
              title={searchQuery ? 'Clear format filter' : 'Filter formats'}
            >
              {searchQuery ? (
                <ClearIcon />
              ) : (
                <SearchIcon className={styles.searchIcon} />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Formats filter panel */}
      <div ref={formatsStageRef} data-testid="file-block-formats-stage" className={styles.formatsStage}>
        <div
          ref={formatsFilterRef}
          id="file-block-formats"
          className={`${styles.formatsFilter} ${showMobileFilters ? styles.showMobileFilters : ''}`}
        >
          <div className={styles.searchContainer}>
            <div className={styles.searchInputWrapper}>
              <input
                type="text"
                placeholder="Filter formats"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
                aria-label="Search formats"
              />
              <button
                type="button"
                className={styles.searchButton}
                onClick={handleClearSearch}
                aria-label={searchQuery ? 'Clear format filter' : 'Filter formats'}
                title={searchQuery ? 'Clear format filter' : 'Filter formats'}
              >
                {searchQuery ? (
                  <ClearIcon />
                ) : (
                  <SearchIcon className={styles.searchIcon} />
                )}
              </button>
            </div>
          </div>
          <div className={styles.filterOptions}>
            {filteredFormats.map((format) => {
              const formatMeta = formatMetaByName.get(format);
              const formatResolution = getFormatResolutionLabel(formatMeta);
              return (
                <label
                  key={format}
                  className={`${styles.filterOption} ${format === 'all' ? styles.filterOptionAll : ''}`}
                >
                  <input
                    type="radio"
                    name="format"
                    value={format}
                    checked={selectedFormat === format}
                    onChange={(e) => handleFormatSelect(e.target.value)}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioLabel}>
                    <span className={styles.radioLabelTitle}>{format === 'all' ? 'All formats' : format}</span>
                    {formatResolution && (
                      <span className={styles.radioLabelMeta}>{formatResolution}</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {recallControlEnabled && recallControlMetrics.width > 0 && (
        <div
          className={`${styles.recallSidebarDock} ${showRecallControl ? styles.recallSidebarDockVisible : styles.recallSidebarDockHidden}`}
          style={{
            top: `${DESKTOP_RECALL_DOCK_TOP}px`,
            left: `${recallControlMetrics.left}px`,
            width: `${recallControlMetrics.width}px`,
          }}
          aria-hidden={!showRecallControl}
        >
          <FormatsRecallControl
            currentFormatLabel={selectedFormatLabel}
            currentFormatResolution={selectedFormatResolution}
            formats={allFormats}
            formatMetaByName={formatMetaByName}
            selectedFormat={selectedFormat}
            onSelectFormat={handleRecallFormatSelect}
            isOpen={isRecallControlOpen}
            onOpen={() => setIsRecallControlOpen(true)}
            onClose={() => setIsRecallControlOpen(false)}
          />
        </div>
      )}

      {/* Assets panel */}
      <div
        ref={assetsListRef}
        className={`${styles.assetsList} ${lockedContainerHeight !== null ? styles.assetsListTransitioning : ''}`}
      >
        <div className={`${styles.assetsHeader} ${styles.hideMobileHeader}`}>
          {appliedFormat !== 'all' && (
            <div className={styles.assetsHeaderFormatBlock}>
              <span className={styles.assetsHeaderFormat}>{appliedFormat}</span>
              {appliedFormatResolution && (
                <span className={styles.assetsHeaderFormatMeta}>{appliedFormatResolution}</span>
              )}
            </div>
          )}
          <div className={styles.assetsHeaderRight}>
            {appliedFormat !== 'all' && (
              <button
                type="button"
                className={`${styles.headerActionBtn} ${styles.headerActionBtnSecondary}`}
                onClick={() => requestFormatChange('all')}
                aria-label="Show all formats"
              >
                Show all formats
              </button>
            )}
            {canDownloadAll && (
              <button
                type="button"
                className={`${styles.headerActionBtn} ${styles.headerActionBtnPrimary}`}
                onClick={handleDownloadAll}
                disabled={isDownloadingAll}
                aria-label={showDownloadAllPreparing ? headerDownloadingLabel : headerDownloadLabel}
              >
                {showDownloadAllPreparing ? headerDownloadingLabel : headerDownloadLabel}
                <img src={DownloadIconSvg} alt="" width={16} height={21} className={styles.downloadAllIcon} />
              </button>
            )}
          </div>
        </div>

        {hasInternalAccess && (
          <div className={styles.internalAccessNotice}>
            Internal access. Files are personally watermarked and sharing outside the company is tracked.
          </div>
        )}

        <div
          key={`assets-grid-${appliedFormat}`}
          className={`${styles.assetsGrid} ${styles.assetsGridMotion} ${isGridEntering ? styles.assetsGridEntering : ''}`}
        >
          {visibleFilteredAssets.length === 0 && searchQuery.trim() ? (
            <div className={styles.noAssetsFound}>No files match this format.</div>
          ) : (() => {
            let lastFormat = null;
            let motionIndex = 0;
            return visibleFilteredAssets.flatMap((asset, index) => {
              const assetId = resolveAssetId(asset);
              const tooltipText = getLockTooltip(asset);
              const tooltipKey = String(assetId ?? `locked-asset-${index}`);
              const tooltipId = `file-block-lock-tooltip-${tooltipKey}`;
              const canDownload = canDownloadAsset(asset);
              const isFree = isAssetFree(asset);
              const resolvedPreviewUrl = resolvePreviewUrlForAsset(asset, singleProduct, previewUrl);
              const isPreviewable = isFree && !!resolvedPreviewUrl && !!onPreviewClick;
              const isGuestFreeAsset = isFree && !isAuthenticated;
              const guestTooltipText = 'Sign in to download';
              const label = getAssetLabel(asset);
              const assetTypeName = getAssetTypeName(asset);
              const suffix = supportsVariantBadges(asset) && variantBadgeAvailabilityByType[assetTypeName]
                ? getAssetSuffix(asset.name, singleProduct?.serial_number)
                : '';
              const ext = getFileExtension(asset.name);
              const size = formatFileSize(asset.file_size);
              const isDownloading = !!downloadingAssets[assetId];
              const showsAsyncStatus = ASYNC_PREPARED_EXTENSIONS.has(ext);
              const downloadBusyLabel = showsAsyncStatus ? 'Preparing file' : 'Downloading file';
              const primaryFormatData = asset.formats?.[0] || null;
              const primaryFormat = getPrimaryFormatName(asset);
              const primaryFormatResolution = getFormatResolutionLabel(primaryFormatData);
              const isFirstGroup = lastFormat === null;
              const showHeader = appliedFormat === 'all' && primaryFormat !== lastFormat;
              if (showHeader) lastFormat = primaryFormat;

              const elements = [];
              if (showHeader && primaryFormat) {
                elements.push(
                  <div
                    key={`header-${primaryFormat}`}
                    className={`${styles.formatGroupHeader} ${styles.motionReveal} ${isFirstGroup ? styles.formatGroupHeaderFirst : ''}`}
                    style={{ '--motion-index': motionIndex }}
                  >
                    <span className={styles.formatGroupHeaderName}>{primaryFormat}</span>
                    {primaryFormatResolution && (
                      <span className={styles.formatGroupHeaderMeta}>{primaryFormatResolution}</span>
                    )}
                  </div>
                );
                motionIndex += 1;
              }

              elements.push(
                <div
                  key={assetId || asset.slug || index}
                  className={`${styles.assetItem} ${styles.motionReveal} ${isPreviewable ? styles.assetItemPreviewable : ''}`}
                  style={{ '--motion-index': motionIndex }}
                  onClick={isPreviewable ? () => onPreviewClick(resolvedPreviewUrl, asset) : undefined}
                  role={isPreviewable ? 'button' : undefined}
                  tabIndex={isPreviewable ? 0 : undefined}
                  onKeyDown={isPreviewable ? (e) => { 
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onPreviewClick(resolvedPreviewUrl, asset);
                    }
                  } : undefined}
                >
                  <div className={styles.assetIcon}>{filesvg}</div>
                  <div className={styles.assetLabelRow}>
                    <span className={styles.assetLabel}>{label}</span>
                    {suffix && (
                      <span className={styles.assetSuffixBadge}>
                        {suffix}
                      </span>
                    )}
                    {isFree && !productIsFree && <span className={styles.freeBadge}>Free</span>}
                    {showsAsyncStatus && (
                      <span
                        className={`${styles.assetPrepStatus} ${isDownloading ? styles.assetPrepStatusVisible : ''}`}
                        aria-hidden={!isDownloading}
                      >
                        Preparing file
                      </span>
                    )}
                  </div>
                  <div className={styles.assetFormat}>
                    {ext && <span className={styles.extBadge}>{ext}</span>}
                    {size && <span className={styles.fileSize}>{size}</span>}
                  </div>
                  <div className={styles.assetAction}>
                    {canDownload ? (
                      <button
                        type="button"
                        className={`${styles.downloadBtn} ${isDownloading ? styles.downloadingBtn : ''}`}
                        onClick={(e) => { e.stopPropagation(); handleDownloadAsset(asset); }}
                        title={isDownloading ? downloadBusyLabel : 'Download file'}
                        disabled={isDownloading}
                        aria-label={isDownloading ? `${downloadBusyLabel}: ${label}` : `Download ${label}`}
                      >
                        {isDownloading ? (
                          <svg width="27" height="27" viewBox="0 0 24 24" fill="none" className={styles.spinnerIcon}>
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="31.4 31.4" strokeLinecap="round" />
                          </svg>
                        ) : (
                          <img src={DownloadIconSvg} alt="Download" width={27} height={36} />
                        )}
                      </button>
                    ) : isGuestFreeAsset ? (
                      <div
                        className={styles.tooltipWrapper}
                        onMouseEnter={() => openLockTooltip(tooltipKey)}
                        onMouseLeave={() => closeLockTooltip(tooltipKey)}
                      >
                        <div onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className={`${styles.downloadBtn} ${styles.disabledDownloadBtn}`}
                            aria-label={`${guestTooltipText} ${label}`}
                            aria-describedby={visibleLockTooltipId === tooltipKey ? tooltipId : undefined}
                            aria-disabled="true"
                            onClick={() => toggleLockTooltip(tooltipKey)}
                            onFocus={() => openLockTooltip(tooltipKey)}
                            onBlur={() => closeLockTooltip(tooltipKey)}
                            onKeyDown={(event) => {
                              if (event.key === 'Escape') {
                                closeLockTooltip(tooltipKey);
                              }
                            }}
                          >
                            <img src={DownloadIconSvg} alt="Download" width={27} height={36} />
                          </button>
                        </div>
                        {visibleLockTooltipId === tooltipKey && (
                          <span
                            id={tooltipId}
                            role="tooltip"
                            className={styles.lockTooltip}
                          >
                            {guestTooltipText}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div
                        className={styles.tooltipWrapper}
                        onMouseEnter={() => openLockTooltip(tooltipKey)}
                        onMouseLeave={() => closeLockTooltip(tooltipKey)}
                      >
                        <button
                          type="button"
                          className={styles.lockBtn}
                          aria-label={tooltipText}
                          aria-describedby={visibleLockTooltipId === tooltipKey ? tooltipId : undefined}
                          onClick={(e) => { e.stopPropagation(); toggleLockTooltip(tooltipKey); }}
                          onFocus={() => openLockTooltip(tooltipKey)}
                          onBlur={() => closeLockTooltip(tooltipKey)}
                          onKeyDown={(event) => {
                            if (event.key === 'Escape') {
                              closeLockTooltip(tooltipKey);
                            }
                          }}
                        >
                          <LockIcon />
                        </button>
                        {visibleLockTooltipId === tooltipKey && (
                          <span
                            id={tooltipId}
                            role="tooltip"
                            className={styles.lockTooltip}
                          >
                            {tooltipText}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
              motionIndex += 1;
              return elements;
            });
          })()}
        </div>
        {appliedFormat === 'all' && remainingFormatGroupCount > 0 && (
          <div className={styles.loadMoreFormatsRow}>
            <button
              type="button"
              className={styles.loadMoreFormatsBtn}
              onClick={handleLoadMoreFormats}
            >
              {loadMoreFormatsLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileBlock;
